/*
  Click and drag to move a horizontal strip.

  A finger already scrolls these strips, so touch pointers are left alone and
  the browser keeps doing what it does. This is for a mouse or a pen, where a
  scroll-snap row otherwise only answers to a trackpad gesture or the arrows.

  Dawn's own scroll settings have to stand down while a drag is running, and
  the strip has to be put back on a slide by hand afterwards, because handing
  snapping back does not re-snap anything. Each step below says why.

  The product image carries a button that opens the zoom view, and every
  thumbnail is a button too. Releasing after a drag would otherwise count as a
  click on one, so the first click after a real drag is swallowed. A plain
  click with no movement still gets through.
*/
(function () {
  const THRESHOLD = 5; // px of movement before a press counts as a drag

  function attach(el) {
    if (!el || el.dataset.smDrag) return;
    el.dataset.smDrag = '';

    /* The grab cursor is a promise, so only make it while the strip has
       somewhere to go. A product with three photos fills its row on a wide
       screen and overflows on a phone, so this is worth rechecking. */
    const mark = () => el.classList.toggle('is-draggable', el.scrollWidth > el.clientWidth + 1);
    mark();
    new ResizeObserver(mark).observe(el);

    let startX = 0;
    let startScroll = 0;
    let pressed = false;
    let dragging = false;

    const release = () => {
      el.style.scrollSnapType = '';
      el.style.scrollBehavior = '';
    };

    /* Where the nearest slide starts. Slides align to the start of the
       scrollport, inset by its scroll padding, which is where the 15px Dawn
       sets would otherwise be lost. */
    const nearestSlide = () => {
      const pad = parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;
      const left = el.getBoundingClientRect().left;
      let target = null;
      let shortest = Infinity;

      for (const child of el.children) {
        const offset = child.getBoundingClientRect().left - left + el.scrollLeft - pad;
        const distance = Math.abs(offset - el.scrollLeft);
        if (distance < shortest) {
          shortest = distance;
          target = offset;
        }
      }

      return target;
    };

    /* Handing snapping back does not re-snap on its own: a strip released
       mid-slide stays mid-slide. So glide to the nearest slide by hand, and
       only give the properties back once we are on it.

       The order matters. Restoring first and scrolling second does not work:
       the snap engine takes the scroll and drops it, leaving the strip where
       it was. This way the animation runs with snapping still off, so nothing
       is there to fight it, and what it restores onto is already a snap
       point. */
    const settle = () => {
      const target = nearestSlide();

      if (target === null || Math.abs(target - el.scrollLeft) < 1) {
        release();
        return;
      }

      el.scrollTo({ left: target, behavior: 'smooth' });

      const done = () => {
        clearTimeout(timer);
        el.removeEventListener('scrollend', done);
        release();
      };
      // scrollend is not everywhere yet, and a scroll can be interrupted.
      const timer = setTimeout(done, 700);
      el.addEventListener('scrollend', done, { once: true });
    };

    const stop = () => {
      if (!pressed) return;
      pressed = false;

      if (!dragging) {
        release();
        return;
      }

      dragging = false;
      el.classList.remove('is-dragging');
      settle();

      /* Swallow the click this release would otherwise produce, so a drag
         across the photos does not open the zoom view and a drag across the
         thumbnails does not pick one.

         On the document, capturing, because that is the first listener to see
         any click. On the strip itself it was not early enough for the
         thumbnails, whose buttons are bound by Dawn's own gallery script.

         Dropped on a timer whether or not a click arrived, so it can never
         eat someone's next real click. */
      const swallow = (e) => {
        e.stopPropagation();
        e.preventDefault();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 0);
    };

    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      if (el.scrollWidth <= el.clientWidth + 1) return; // nothing to scroll
      pressed = true;
      startX = e.clientX;
      startScroll = el.scrollLeft;

      /* Two of Dawn's scroll properties have to stand down for the duration.

         Snapping, because writing scrollLeft against `x mandatory` pulls the
         strip back to the slide it started on, so it never tracks the cursor.

         Smooth scrolling, because it turns every write into an animation:
         each move restarts one from wherever the last had reached, the strip
         crawls, and it keeps gliding after the mouse is long released.

         Both go off on the press rather than on the first move, so the change
         has a frame to take hold; reading a layout property forces that
         along. They are handed back on release, which is when smooth is
         wanted, for the glide onto the nearest slide. */
      el.style.scrollSnapType = 'none';
      el.style.scrollBehavior = 'auto';
      void el.offsetHeight;
    });

    el.addEventListener('pointermove', (e) => {
      if (!pressed) return;
      const dx = e.clientX - startX;

      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        el.classList.add('is-dragging');
        try {
          el.setPointerCapture(e.pointerId);
        } catch (err) {
          /* The pointer is already gone. The drag still works without it. */
        }
      }

      el.scrollLeft = startScroll - dx;
      e.preventDefault();
    });

    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);
    /* A drag that leaves the window never reports a release. */
    window.addEventListener('blur', stop);

    /* An image is draggable by default, which starts a file drag instead. */
    el.addEventListener('dragstart', (e) => {
      if (dragging) e.preventDefault();
    });
  }

  function refresh() {
    document.querySelectorAll('.product__media-list, .thumbnail-list').forEach(attach);
  }

  window.smDragScroll = { refresh, attach };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  /* The gallery is re-rendered on a variant change. */
  document.addEventListener('shopify:section:load', refresh);
})();
